import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getJobProfiles, getFinalizedProfileTemplates } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppPagination } from '@/stores/app/pagination';

export const useJobProfiles = defineStore('jobProfiles', () => {
  const { setLoading } = useAppLoading();

  const { selectedSubMenuOption } = storeToRefs(useSubMenu());
  const { setSelectedSubMenuOption } = useSubMenu();

  const jobProfiles = ref([]);
  const sortOptions = ref([]);
  const textFilter = ref('');
  const finalizedTemplates = ref([]);
  const selectedProfiles = ref(new Set());

  const updateSortOptions = (newSortOptions) => {
    sortOptions.value = newSortOptions;
  };

  const resetSelectedProfiles = () => {
    selectedProfiles.value.clear();
  };

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const loadJobProfiles = async ({
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = '-effective_date';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params.page = page || undefined;

      const parameters = {
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };
      const newJobProfiles = await getJobProfiles(parameters);

      jobProfiles.value = newJobProfiles.documents;

      updateSortOptions((newJobProfiles?.sorting?.options || []).map(({ id }) => id));
      setSelectedSubMenuOption(newJobProfiles?.sorting?.value);
      useAppPagination().updatePagination(newJobProfiles.pagination);

      if (newJobProfiles?.textFilter?.value) {
        setTextFilter(newJobProfiles.textFilter.value);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profiles load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const loadFinalizedProfileTemplates = async () => {
    setLoading({ listLoading: true });
    try {
      const { templates } = await getFinalizedProfileTemplates();
      finalizedTemplates.value = templates;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Finalized templates load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  return {
    // State
    jobProfiles,
    sortOptions,
    textFilter,
    finalizedTemplates,
    selectedProfiles,

    // Mutations
    updateSortOptions,
    setTextFilter,

    // Actions
    loadJobProfiles,
    loadFinalizedProfileTemplates,
    resetSelectedProfiles,
  };
});
